/**
 * Hub Callback Page for AdCanvas
 *
 * Handles the OAuth-style callback from the Central Hub.
 * Exchanges the authorization code for tokens and redirects to the dashboard.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  exchangeCodeForTokens,
  storeTokens,
  getAuthCodeFromUrl,
  getPlatformAccess,
} from '../../lib/hub';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Loader2, AlertCircle } from 'lucide-react';

export default function HubCallbackPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'error' | 'success'>('loading');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      const code = getAuthCodeFromUrl();

      if (!code) {
        setError('No authorization code provided');
        setStatus('error');
        return;
      }

      try {
        // Exchange the code for tokens
        const tokens = await exchangeCodeForTokens(code);

        if (!tokens) {
          setError('Failed to exchange authorization code');
          setStatus('error');
          return;
        }

        // Store the tokens
        storeTokens(tokens.access_token, tokens.refresh_token);

        // Verify we have access to this platform (cryptographically verifies the
        // hub token signature against the hub JWKS before trusting any claim).
        const platformAccess = await getPlatformAccess();
        if (!platformAccess) {
          setError('You do not have access to AdCanvas');
          setStatus('error');
          return;
        }

        // Store organization ID for other components
        localStorage.setItem('selectedOrgId', platformAccess.organization_id);

        setStatus('success');

        // Clean up URL and redirect to dashboard
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 500);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Authentication failed';
        setError(errorMessage);
        setStatus('error');
      }
    };

    handleCallback();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center">
            {status === 'loading' && 'Signing you in...'}
            {status === 'success' && 'Welcome back!'}
            {status === 'error' && 'Sign in failed'}
          </CardTitle>
          <CardDescription className="text-center">
            {status === 'loading' && 'Please wait while we complete your sign in.'}
            {status === 'success' && 'Redirecting to your dashboard...'}
            {status === 'error' && 'There was a problem signing you in.'}
          </CardDescription>
        </CardHeader>

        <CardContent>
          {status === 'loading' && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          )}

          {status === 'success' && (
            <div className="flex items-center justify-center py-8">
              <div className="h-8 w-8 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="space-y-4">
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
              <Button
                className="w-full"
                onClick={() => navigate('/login', { replace: true })}
              >
                Try Again
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
